import React, { useState, FormEvent, ChangeEvent } from 'react';
import { supabase } from '../supabase/supabaseClient';

interface FormData {
  owner_name: string;
  phone_number: string;
  email: string;
  cafe_name: string;
  location: string;
}

interface Message {
  text: string;
  type: 'success' | 'error' | '';
}

interface CafeOwnerFormProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function CafeOwnerForm({ isOpen, setIsOpen }: CafeOwnerFormProps) {
  const [formData, setFormData] = useState<FormData>({
    owner_name: '',
    phone_number: '',
    email: '',
    cafe_name: '',
    location: ''
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<Message>({ text: '', type: '' });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const { data, error } = await supabase
        .from('cafe_owners')
        .insert([formData])
        .select();

      if (error) throw error;

      setMessage({ 
        text: 'Cafe owner data saved successfully!', 
        type: 'success' 
      });
      
      // Reset form
      setFormData({
        owner_name: '',
        phone_number: '',
        email: '',
        cafe_name: '',
        location: ''
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setIsOpen(false);
        setMessage({ text: '', type: '' });
      }, 2000);

    } catch (error) {
      console.error('Error saving data:', error);
      setMessage({ 
        text: `Error: ${(error as Error).message}`, 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      owner_name: '',
      phone_number: '',
      email: '',
      cafe_name: '',
      location: ''
    });
    setMessage({ text: '', type: '' });
  };

  const closeModal = () => {
    setIsOpen(false);
    handleReset();
  };

  return (
    <>
      {/* Modal Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-neutral-100 px-8 py-6 rounded-t-3xl flex items-center justify-between">
              <div>
                {/* <h2 className="text-2xl font-semibold tracking-tight">
                  Register Your Cafe
                </h2> */}
                <p className="text-neutral-600 text-sm mt-1">
                  Fill in the details to get started
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-neutral-400 hover:text-neutral-900 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="px-8 py-8 space-y-6">
              {/* Owner Name */}
              <div>
                <label 
                  htmlFor="owner_name" 
                  className="block text-sm font-medium text-neutral-900 mb-2"
                >
                  Owner Name *
                </label>
                <input
                  type="text"
                  id="owner_name"
                  name="owner_name"
                  required
                  value={formData.owner_name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition text-sm"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label 
                  htmlFor="phone_number" 
                  className="block text-sm font-medium text-neutral-900 mb-2"
                >
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  placeholder="+1234567890"
                  className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition text-sm"
                />
              </div>

              {/* Email */}
              <div>
                <label 
                  htmlFor="email" 
                  className="block text-sm font-medium text-neutral-900 mb-2"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition text-sm"
                />
              </div>

              {/* Cafe Name */}
              <div>
                <label 
                  htmlFor="cafe_name" 
                  className="block text-sm font-medium text-neutral-900 mb-2"
                >
                  Cafe Name *
                </label>
                <input
                  type="text"
                  id="cafe_name"
                  name="cafe_name"
                  required
                  value={formData.cafe_name}
                  onChange={handleChange}
                  placeholder="Coffee Haven"
                  className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition text-sm"
                />
              </div>

              {/* Location */}
              <div>
                <label 
                  htmlFor="location" 
                  className="block text-sm font-medium text-neutral-900 mb-2"
                >
                  Location
                </label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="123 Main St, New York"
                  className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:border-transparent transition text-sm"
                />
              </div>

              {/* Message */}
              {message.text && (
                <div 
                  className={`p-4 rounded-xl text-sm border ${
                    message.type === 'success' 
                      ? 'bg-green-50 text-green-800 border-green-200' 
                      : 'bg-red-50 text-red-800 border-red-200'
                  }`}
                >
                  {message.text}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-neutral-900 hover:bg-neutral-800 text-white font-medium py-3.5 px-6 rounded-full transition-all disabled:bg-neutral-400 disabled:cursor-not-allowed text-sm hover:shadow-lg"
                >
                  {loading ? 'Saving...' : 'Save Cafe Owner'}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-6 py-3.5 border-2 border-neutral-200 hover:border-neutral-900 text-neutral-900 text-sm font-medium rounded-full transition-all hover:bg-neutral-50"
                >
                  Clear
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}